/**
 * PotApprovalListItem Molecule
 * Displays a single pot row in the approval list
 * Pattern: Atomic Design - Molecules, follows SiteListItem / UserListItem styling
 */

import React, { useCallback } from 'react';
import {
  Button,
  CalendarClockIcon,
  ChevronRightIcon,
  Fact,
  LedgerHoverCard,
  LedgerRow,
  ScheduleSendIcon,
  StarIcon,
  StatusDot,
  Tooltip,
  WarningIcon,
  statusToken,
  showBelowWidthClass,
  type LedgerCollapse,
  type LedgerColumn,
} from '@billda/design-system';
import { formatCurrency } from '../../../shared/utils/currency';
import { PotActionsMenu } from './PotActionsMenu';
import type { PotSummary } from '../types/pot.types';

export interface PotApprovalListItemProps {
  pot: PotSummary;
  columns: readonly LedgerColumn[];
  collapse: LedgerCollapse;
  onClick?: () => void;
  onMoveToActivePeriod?: (potId: string) => void;
  onMoveToPrimary?: (potId: string) => void;
  onExtendDeadline?: (potId: string) => void;
  'data-testid'?: string;
}

function formatPeriod(start: string, end: string): string {
  const startDate = new Date(start);
  const endDate = new Date(end);
  const startLabel = new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
  }).format(startDate);
  const endLabel = new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(endDate);
  return `${startLabel}–${endLabel}`;
}

function formatDeadline(pot: PotSummary): string {
  if (!pot.nextDeadline.date || !pot.nextDeadline.type) {
    return '—';
  }
  const date = new Date(pot.nextDeadline.date);
  const dateLabel = Number.isNaN(date.getTime())
    ? pot.nextDeadline.date
    : new Intl.DateTimeFormat('en-GB', {
        day: 'numeric',
        month: 'short',
      }).format(date);
  return `${pot.nextDeadline.type} · ${dateLabel}`;
}

function PotFlag({
  label,
  testId,
  children,
}: {
  label: string;
  testId?: string;
  children: React.ReactNode;
}) {
  return (
    <Tooltip content={label}>
      <span
        role="img"
        aria-label={label}
        tabIndex={0}
        className="inline-flex"
        data-testid={testId}
      >
        {children}
      </span>
    </Tooltip>
  );
}

function PotFlags({ pot }: { pot: PotSummary }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      {pot.isPrimary && (
        <PotFlag label="Primary pot">
          <StarIcon
            aria-hidden="true"
            className="size-3.5 text-content-accent"
          />
        </PotFlag>
      )}
      {pot.isScheduledForAutoSubmit && (
        <PotFlag label="Scheduled for automatic submission">
          <ScheduleSendIcon
            aria-hidden="true"
            className="size-3.5 text-content-secondary"
          />
        </PotFlag>
      )}
      {pot.wasLate && (
        <PotFlag label="Missed its submit deadline" testId="pot-late-chip">
          <WarningIcon
            aria-hidden="true"
            className="size-3.5 text-content-warning"
          />
        </PotFlag>
      )}
      {pot.submitDeadlineExtendedUntil && (
        <PotFlag label="Submit deadline extended">
          <CalendarClockIcon
            aria-hidden="true"
            className="size-3.5 text-content-info"
          />
        </PotFlag>
      )}
    </span>
  );
}

export const PotApprovalListItem = React.memo(function PotApprovalListItem({
  pot,
  columns,
  collapse,
  onClick,
  onMoveToActivePeriod,
  onMoveToPrimary,
  onExtendDeadline,
  'data-testid': dataTestId,
}: PotApprovalListItemProps) {
  const handleClick = useCallback(() => onClick?.(), [onClick]);
  const baseTestId = dataTestId ?? `pot-${pot.id}`;
  const net = formatCurrency(pot.netAmount);
  const deadline = formatDeadline(pot);
  const period = `Week ${pot.period.weekNumber} · ${formatPeriod(pot.period.start, pot.period.end)}`;
  const status = statusToken(pot.status);

  return (
    <LedgerRow
      columns={columns}
      collapse={collapse}
      lead={<StatusDot color={status.color} />}
      title={pot.owner.name}
      titleTag={
        <span
          className={showBelowWidthClass(
            collapse.container,
            collapse.at,
            'inline-flex'
          )}
        >
          <PotFlags pot={pot} />
        </span>
      }
      cells={{
        type: pot.owner.type,
        net: <span className="font-semibold text-content-primary">{net}</span>,
        deadline,
        flags: <PotFlags pot={pot} />,
      }}
      meta={`${status.word} · ${pot.owner.type} · ${deadline}`}
      showValues={false}
      hoverCard={
        <LedgerHoverCard
          title={pot.owner.name}
          kind="Pot"
          onOpenDetails={handleClick}
          openDetailsLabel="Open pot"
        >
          <Fact label="Owner type">{pot.owner.type}</Fact>
          <Fact label="Status">{status.word}</Fact>
          <Fact label="Period">{period}</Fact>
          <Fact label="Net">{net}</Fact>
          <Fact label="Primary">{pot.isPrimary ? 'Yes' : 'No'}</Fact>
          <Fact label="Submission">
            {pot.isScheduledForAutoSubmit ? 'Automatic' : 'Manual'}
          </Fact>
          <Fact label="Next deadline">{deadline}</Fact>
        </LedgerHoverCard>
      }
      actions={
        <PotActionsMenu
          potId={pot.id}
          status={pot.status}
          isPrimary={pot.isPrimary}
          submitDeadlineExtendedUntil={pot.submitDeadlineExtendedUntil}
          onMoveToActivePeriod={onMoveToActivePeriod}
          onMoveToPrimary={onMoveToPrimary}
          onExtendDeadline={onExtendDeadline}
          data-testid={`${baseTestId}-actions`}
          fallback={
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClick}
              aria-label={`Open ${pot.owner.name} pot`}
              className="size-8"
            >
              <ChevronRightIcon aria-hidden="true" className="size-4" />
            </Button>
          }
        />
      }
      onClick={handleClick}
      aria-label={`Open ${pot.owner.name} ${status.word} pot`}
      data-testid={`${baseTestId}-row`}
    />
  );
});
